import { Injectable, OnDestroy } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import * as RiderAuthenticationActions from '@uzurv/rideWss/app/store/rider-authentication/rider-authentication.action';
import { isNullOrUndefined } from '@uzurv/rideWss/app/util';
import { RiderService, RuleValue, RuleValueConstants } from '@uzurv/rideWss/clara';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthenticatedRiderService } from '../authenticated-rider/authenticated-rider.service';

@Injectable({
  providedIn: 'root'
})
export class RuleValueService implements OnDestroy {
  private _ruleValues: Array<RuleValue> = [];

  readonly destroyed$ = new Subject<boolean>();

  constructor(private actions$: Actions, private authenticatedRiderService: AuthenticatedRiderService, private riderService: RiderService) {
    if (this.authenticatedRiderService.isLoggedIn()) {
      this.refreshRuleValues();

      // TODO(Ride WSS): Do we need a polling mechanism? This used to refresh on app resume.
    }

    this.actions$.pipe(ofType(RiderAuthenticationActions.riderAuthenticationComplete), takeUntil(this.destroyed$)).subscribe(action => {
      this.refreshRuleValues();
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private async refreshRuleValues() {
    try {
      this._ruleValues = await firstValueFrom(this.riderService.getRuleValues());
    } catch (err) {
      // Do Nothing, defaults will be used
    }
  }

  getRuleValues(): Array<RuleValue> {
    return this._ruleValues;
  }

  getAuthorizeNetValidationMode(): string {
    if (isNullOrUndefined(this.getRuleValues())) {
      return 'testMode';
    }

    const ruleValue: RuleValue = this.getRuleValues().find(
      ruleValue => ruleValue.name === RuleValueConstants.AUTHORIZE_NET_VALIDATION_MODE
    );

    if (isNullOrUndefined(ruleValue)) {
      return 'testMode';
    }

    return ruleValue.value;
  }

  getDaysBeforeAppDeprecationToWarnUser(): number {
    if (isNullOrUndefined(this.getRuleValues())) {
      return RuleValueConstants.DAYS_BEFORE_APP_DEPRECATION_TO_WARN_USER_DEFAULT;
    }

    const ruleValue: RuleValue = this.getRuleValues().find(
      ruleValue => ruleValue.name === RuleValueConstants.DAYS_BEFORE_APP_DEPRECATION_TO_WARN_USER
    );

    if (isNullOrUndefined(ruleValue)) {
      return RuleValueConstants.DAYS_BEFORE_APP_DEPRECATION_TO_WARN_USER_DEFAULT;
    }

    return parseInt(ruleValue.value, 10);
  }

  getMinutesToDelayPickUpTimeForRideBackupSelfService(): number {
    if (isNullOrUndefined(this.getRuleValues())) {
      return RuleValueConstants.MINUTES_TO_DELAY_PICK_UP_TIME_FOR_RIDE_BACKUP_SELF_SERVICE_DEFAULT;
    }

    const ruleValue: RuleValue = this.getRuleValues().find(
      ruleValue => ruleValue.name === RuleValueConstants.MINUTES_TO_DELAY_PICK_UP_TIME_FOR_RIDE_BACKUP_SELF_SERVICE
    );

    if (isNullOrUndefined(ruleValue)) {
      return RuleValueConstants.MINUTES_TO_DELAY_PICK_UP_TIME_FOR_RIDE_BACKUP_SELF_SERVICE_DEFAULT;
    }

    return parseInt(ruleValue.value, 10);
  }

  getDaysToWaitBetweenPromptingRiderToClaimOrgRiders(): number {
    if (isNullOrUndefined(this.getRuleValues())) {
      return RuleValueConstants.DAYS_TO_WAIT_BETWEEN_PROMPTING_RIDER_TO_CLAIM_ORG_RIDERS_DEFAULT;
    }

    const ruleValue: RuleValue = this.getRuleValues().find(
      ruleValue => ruleValue.name === RuleValueConstants.DAYS_TO_WAIT_BETWEEN_PROMPTING_RIDER_TO_CLAIM_ORG_RIDERS
    );

    if (isNullOrUndefined(ruleValue)) {
      return RuleValueConstants.DAYS_TO_WAIT_BETWEEN_PROMPTING_RIDER_TO_CLAIM_ORG_RIDERS_DEFAULT;
    }

    return parseInt(ruleValue.value, 10);
  }

  getMaximumAgeToRegisterInRiderApp(): number {
    if (isNullOrUndefined(this.getRuleValues())) {
      return RuleValueConstants.MAXIMUM_AGE_TO_REGISTER_IN_RIDER_APP_DEFAULT;
    }

    const ruleValue: RuleValue = this.getRuleValues().find(
      ruleValue => ruleValue.name === RuleValueConstants.MAXIMUM_AGE_TO_REGISTER_IN_RIDER_APP
    );

    if (isNullOrUndefined(ruleValue)) {
      return RuleValueConstants.MAXIMUM_AGE_TO_REGISTER_IN_RIDER_APP_DEFAULT;
    }

    return parseInt(ruleValue.value, 10);
  }

  getMinimumAgeToRegisterInRiderApp(): number {
    if (isNullOrUndefined(this.getRuleValues())) {
      return RuleValueConstants.MINIMUM_AGE_TO_REGISTER_IN_RIDER_APP_DEFAULT;
    }

    const ruleValue: RuleValue = this.getRuleValues().find(
      ruleValue => ruleValue.name === RuleValueConstants.MINIMUM_AGE_TO_REGISTER_IN_RIDER_APP
    );

    if (isNullOrUndefined(ruleValue)) {
      return RuleValueConstants.MINIMUM_AGE_TO_REGISTER_IN_RIDER_APP_DEFAULT;
    }

    return parseInt(ruleValue.value, 10);
  }
}
